import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImpactChart = () => {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('monthly');

  const monthlyData = [
    { month: 'Ene', foodRescued: 12, waterSaved: 180, co2Avoided: 8.5 },
    { month: 'Feb', foodRescued: 18, waterSaved: 270, co2Avoided: 12.8 },
    { month: 'Mar', foodRescued: 25, waterSaved: 375, co2Avoided: 17.5 },
    { month: 'Abr', foodRescued: 32, waterSaved: 480, co2Avoided: 22.4 },
    { month: 'May', foodRescued: 28, waterSaved: 420, co2Avoided: 19.6 },
    { month: 'Jun', foodRescued: 35, waterSaved: 525, co2Avoided: 24.5 },
    { month: 'Jul', foodRescued: 42, waterSaved: 630, co2Avoided: 29.4 },
    { month: 'Ago', foodRescued: 38, waterSaved: 570, co2Avoided: 26.6 },
    { month: 'Sep', foodRescued: 45, waterSaved: 675, co2Avoided: 31.5 },
    { month: 'Oct', foodRescued: 52, waterSaved: 780, co2Avoided: 36.4 },
    { month: 'Nov', foodRescued: 48, waterSaved: 720, co2Avoided: 33.6 }
  ];

  const quarterlyData = [
    { quarter: 'Q1 2024', foodRescued: 55, waterSaved: 825, co2Avoided: 38.5 },
    { quarter: 'Q2 2024', foodRescued: 95, waterSaved: 1425, co2Avoided: 66.5 },
    { quarter: 'Q3 2024', foodRescued: 125, waterSaved: 1875, co2Avoided: 87.5 },
    { quarter: 'Q4 2024', foodRescued: 100, waterSaved: 1500, co2Avoided: 70.0 }
  ];

  const yearlyData = [
    { year: '2022', foodRescued: 145, waterSaved: 2175, co2Avoided: 101.5 },
    { year: '2023', foodRescued: 285, waterSaved: 4275, co2Avoided: 199.5 },
    { year: '2024', foodRescued: 375, waterSaved: 5625, co2Avoided: 262.5 }
  ];

  const getCurrentData = () => {
    switch (timeRange) {
      case 'quarterly':
        return quarterlyData;
      case 'yearly':
        return yearlyData;
      default:
        return monthlyData;
    }
  };

  const getXAxisKey = () => {
    switch (timeRange) {
      case 'quarterly':
        return 'quarter';
      case 'yearly':
        return 'year';
      default:
        return 'month';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-warm">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value} {entry?.dataKey === 'foodRescued' ? 'kg' : entry?.dataKey === 'waterSaved' ? 'L' : 'kg CO₂'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Progreso de Impacto Ambiental
        </h3>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <div className="flex space-x-1 bg-muted rounded-md p-1">
            <Button
              variant={timeRange === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeRange('monthly')}
              className="text-xs"
            >
              Mensual
            </Button>
            <Button
              variant={timeRange === 'quarterly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeRange('quarterly')}
              className="text-xs"
            >
              Trimestral
            </Button>
            <Button
              variant={timeRange === 'yearly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeRange('yearly')}
              className="text-xs"
            >
              Anual
            </Button>
          </div>
          
          <div className="flex space-x-1 bg-muted rounded-md p-1">
            <Button
              variant={chartType === 'line' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setChartType('line')}
            >
              <Icon name="TrendingUp" size={16} />
            </Button>
            <Button
              variant={chartType === 'bar' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setChartType('bar')}
            >
              <Icon name="BarChart3" size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={getCurrentData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey={getXAxisKey()} 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="foodRescued" 
                stroke="var(--color-success)" 
                strokeWidth={3}
                name="Comida Rescatada"
                dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="co2Avoided" 
                stroke="var(--color-accent)" 
                strokeWidth={3}
                name="CO₂ Evitado"
                dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          ) : (
            <BarChart data={getCurrentData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey={getXAxisKey()} 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="foodRescued" 
                fill="var(--color-success)" 
                name="Comida Rescatada"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-success"></div>
          <span className="text-muted-foreground">Comida Rescatada (kg)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-accent"></div>
          <span className="text-muted-foreground">CO₂ Evitado (kg)</span>
        </div>
      </div>
    </div>
  );
};

export default ImpactChart;